// Administracion -> Automatizaciones: correos que salen solos (recordatorio de citas y respuestas de los casos).
// Todas apagadas hasta que un administrador las activa a conciencia: salen correos reales a los clientes.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { type Automatizacion, AUTOMATIZACIONES, automatizaciones, type ClaveAutomatizacion, correosValidos, EJEMPLO, guardarAutomatizacion, rellenar } from '../../api/automatizaciones';
import { firmaDe } from '../../lib/correo';
import { usePlantillas } from '../../ui/Plantillas';
import { cuerpoHtml, ejemploInterno, EJEMPLO_LICENCIAS, tablaHtml, tablaInternaHtml } from '../../lib/correoLicencias';
import { ErrorBox, Pill, Skeleton } from '../../ui/bits';
import { Antes, BarraCambios } from '../../ui/BarraCambios';
import type { CampoCambio } from '../../lib/cambiosPendientes';
import { useCambiosPendientes } from '../../lib/useCambiosPendientes';
import { toast } from '../../ui/toasts';
import { enviar } from '../../api/correo';
import { useSession } from '../../auth/session';
import { useBuzonesGrupo } from './BuzonesGrupo';
import { type AutoCreada } from '../../api/automatizacionesCreadas';
import { NuevaAutomatizacion, RestaurarCreada, TarjetaCreada, useCreadas } from './AutomatizacionesCreadas';

export const useAutomatizaciones = () => useQuery({ queryKey: ['automatizaciones'], queryFn: automatizaciones, staleTime: 60_000 });

export function Automatizaciones() {
  const r = useAutomatizaciones();
  const creadas = useCreadas();
  if (r.error) return <ErrorBox error={r.error} onRetry={() => r.refetch()} />;
  if (!r.data) return <Skeleton rows={6} />;
  const claves = Object.keys(AUTOMATIZACIONES) as ClaveAutomatizacion[];
  const borradas = claves.filter((k) => r.data[k].oculta);
  const creadasBorradas = (creadas.data ?? []).filter((a) => a.oculta);
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <NuevaAutomatizacion />
      </div>
      {(creadas.data ?? [])
        .filter((a) => !a.oculta)
        .map((a) => (
          <TarjetaCreada key={a.id} guardada={a} />
        ))}
      {claves
        .filter((k) => !r.data[k].oculta)
        .map((k) => (
          <Tarjeta key={k} guardada={r.data[k]} />
        ))}
      {borradas.length || creadasBorradas.length ? <Borradas claves={borradas} datos={r.data} creadas={creadasBorradas} /> : null}
    </div>
  );
}

// Automatizaciones borradas: apagadas y fuera de la lista, con su texto guardado para restaurarlas.
function Borradas({ claves, datos, creadas }: { claves: ClaveAutomatizacion[]; datos: Record<ClaveAutomatizacion, Automatizacion>; creadas: AutoCreada[] }) {
  const qc = useQueryClient();
  const restaurar = async (k: ClaveAutomatizacion) => {
    try {
      await guardarAutomatizacion({ ...datos[k], oculta: false, activa: false });
      await qc.invalidateQueries({ queryKey: ['automatizaciones'] });
      toast(`${AUTOMATIZACIONES[k].titulo}: restaurada (apagada).`);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  return (
    <section className="box" style={{ display: 'grid', gap: 8 }}>
      <h2 className="lbl" style={{ margin: 0 }}>Automatizaciones borradas</h2>
      <p className="note" style={{ margin: 0 }}>No envían nada. Al restaurarlas vuelven apagadas y con el texto que tenían.</p>
      {claves.map((k) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flex: 1 }}>{AUTOMATIZACIONES[k].titulo}</span>
          <button className="btn sm" onClick={() => void restaurar(k)}>
            Restaurar
          </button>
        </div>
      ))}
      {creadas.map((a) => (
        <RestaurarCreada key={a.id} a={a} />
      ))}
    </section>
  );
}

// Campos que cuenta la barra de cambios sin guardar, segun lo que muestra cada tarjeta.
const horaTxt = (h: unknown) => (h == null ? '(por defecto)' : `${h}:00`);
function camposAutomatizacion(d: { conBuzon: boolean; programacion?: boolean; interno?: boolean }): CampoCambio<Automatizacion>[] {
  const c: CampoCambio<Automatizacion>[] = [];
  if (d.conBuzon) c.push({ id: 'buzon', t: 'Enviar desde' });
  if (d.programacion)
    c.push(
      { id: 'diasPrimer', t: 'Primer aviso (días)' },
      { id: 'diasSegundo', t: 'Segundo aviso (días)' },
      { id: 'hora', t: 'Hora de envío', fmt: horaTxt },
      { id: 'copiasPrimer', t: 'Copias del primer aviso' },
      { id: 'copiasSegundo', t: 'Copias del segundo aviso' },
    );
  if (d.interno) c.push({ id: 'destinatarios', t: 'Enviar a' }, { id: 'diasPrimer', t: 'Avisar (días antes)' }, { id: 'hora', t: 'Hora de envío', fmt: horaTxt });
  c.push({ id: 'asunto', t: 'Asunto' }, { id: 'cuerpo', t: 'Texto' }, { id: 'activa', t: 'Enviar automáticamente', fmt: (v) => (v ? 'Encendido' : 'Apagado') });
  return c;
}

function Tarjeta({ guardada }: { guardada: Automatizacion }) {
  const qc = useQueryClient();
  const d = AUTOMATIZACIONES[guardada.clave];
  const grupos = useBuzonesGrupo();
  const [v, setV] = useState(guardada);
  const [abierta, setAbierta] = useState(false);
  const [entendido, setEntendido] = useState(false);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => setV(guardada), [guardada]);

  const compartidos = (grupos.data ?? []).filter((g) => g.cuentaId && g.activo);
  const activando = v.activa && !guardada.activa;
  const primer = v.diasPrimer ?? 45;
  const segundo = v.diasSegundo ?? 25;
  const horaOk = v.hora == null || (Number.isInteger(v.hora) && v.hora >= 0 && v.hora <= 23);
  const correosOk = !d.interno || v.destinatarios.split(/[,;\s]+/).filter(Boolean).every((c) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c)) && !!v.destinatarios.trim();
  const diasInterno = v.diasPrimer ?? 15;
  const tramosOk = (!d.programacion || (primer > 0 && primer <= 90 && segundo >= 0 && segundo < primer && horaOk)) && (!d.interno || (diasInterno > 0 && diasInterno <= 90 && correosOk));
  const copiasOk = !d.programacion || (correosValidos(v.copiasPrimer) && correosValidos(v.copiasSegundo));
  const puedeGuardar = (!d.conBuzon || !!v.buzon) && !!v.cuerpo.trim() && (!activando || entendido) && tramosOk && copiasOk;
  const num = (t: string) => (t.trim() === '' ? null : Number(t));
  const id = (x: string) => `${guardada.clave}-${x}`;
  // Correos de licencias: vista previa en HTML (tabla y firma del buzon elegido), igual a como llega.
  const esLicencia = guardada.clave.startsWith('licencia');
  // Misma firma que usara el servidor: la del buzon de grupo o, si no tiene, su plantilla de firma (sin la personal).
  const plantillas = usePlantillas();
  const firmaBuzon =
    compartidos.find((g) => g.correo.toLowerCase() === v.buzon)?.firma ||
    firmaDe(v.buzon, (plantillas.data ?? []).filter((x) => x.tipo === 'FIRMA' && !x.duenoId), '');
  // El ejemplo sigue a los dias elegidos en la tarjeta, para que la vista previa diga lo mismo que llegara.
  const lineasInterno = ejemploInterno(diasInterno);
  const datosPrevia = d.interno ? { ...EJEMPLO, dias: String(diasInterno), total: String(lineasInterno.length) } : { ...EJEMPLO, dias: String(v.diasPrimer ?? 25) };
  const htmlPrevia = esLicencia
    ? cuerpoHtml(v.cuerpo, datosPrevia, guardada.clave === 'licenciaInterna' ? tablaInternaHtml(lineasInterno) : tablaHtml(EJEMPLO_LICENCIAS, guardada.clave === 'licenciaEN' ? 'EN' : 'ES'), firmaBuzon)
    : null;

  const { yo } = useSession();
  const [paraPrueba, setParaPrueba] = useState('');
  const [probando, setProbando] = useState(false);
  const buzonElegido = compartidos.find((g) => g.correo.toLowerCase() === v.buzon);
  // Prueba: el correo tal como saldria (con datos de ejemplo) desde el buzon elegido, a quien se indique.
  const enviarPrueba = async () => {
    const para = (paraPrueba || yo.email || '').trim();
    if (!buzonElegido?.cuentaId || !para) return;
    setProbando(true);
    try {
      await enviar({
        cuentaId: buzonElegido.cuentaId,
        para: [para],
        asunto: `[Prueba] ${rellenar(v.asunto, datosPrevia)}`,
        texto: rellenar(v.cuerpo, datosPrevia),
        firma: htmlPrevia ? undefined : firmaBuzon || undefined,
        html: htmlPrevia ?? undefined,
      });
      toast(`Prueba enviada a ${para} desde ${buzonElegido.correo}.`);
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setProbando(false);
    }
  };
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const borrar = async () => {
    if (!confirmarBorrar) return setConfirmarBorrar(true);
    try {
      await guardarAutomatizacion({ ...guardada, oculta: true, activa: false });
      await qc.invalidateQueries({ queryKey: ['automatizaciones'] });
      toast(`${d.titulo}: borrada. La puedes restaurar al final de la lista.`);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  const guardar = async () => {
    setGuardando(true);
    try {
      await guardarAutomatizacion(v);
      await qc.invalidateQueries({ queryKey: ['automatizaciones'] });
      setEntendido(false);
      setAbierta(false);
      toast(v.activa ? `${d.titulo}: activo.` : `${d.titulo}: guardado (apagado).`);
      return true;
    } catch (e) {
      toast((e as Error).message, 'error');
      return false;
    } finally {
      setGuardando(false);
    }
  };
  // Barra de cambios sin guardar (cuenta, pregunta al salir y guarda con Ctrl+S).
  const campos = useMemo(() => camposAutomatizacion(d), [d]);
  const c = useCambiosPendientes<Automatizacion>({
    titulo: d.titulo,
    inicial: guardada,
    actual: v,
    campos,
    poner: (x) => setV((prev) => ({ ...prev, ...x })),
    guardar,
    invalido: puedeGuardar
      ? null
      : d.conBuzon && !v.buzon
        ? 'elige el buzón que envía'
        : !v.cuerpo.trim()
          ? 'escribe el texto'
          : !copiasOk
            ? 'revisa las copias'
            : !tramosOk
              ? 'revisa los días y los correos'
              : 'confirma que entiendes que saldrán correos',
  });

  return (
    <section className="box" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>{d.titulo}</h2>
        {guardada.activa ? <Pill tone="ok">Activo</Pill> : <Pill>Apagado</Pill>}
        <button className="btn sm ghost" style={{ marginLeft: 'auto' }} onClick={() => setAbierta(!abierta)} aria-expanded={abierta}>
          {abierta ? 'Cerrar' : 'Configurar'}
        </button>
      </div>
      <p className="note" style={{ margin: 0 }}>
        {d.cuando}
      </p>
      {abierta ? (
        <div className="form">
          {d.conBuzon ? (
            <label className={c.clase('buzon')}>
              Enviar desde
              <select id={id('buzon')} value={v.buzon} onChange={(e) => setV({ ...v, buzon: e.target.value })}>
                <option value="">Elige un buzón de grupo</option>
                {compartidos.map((g) => (
                  <option key={g.id} value={g.correo.toLowerCase()}>
                    {g.nombre} · {g.correo}
                  </option>
                ))}
              </select>
              <Antes c={c} campo="buzon" />
            </label>
          ) : null}
          {d.programacion ? (
            <div className="programacion">
              <label className={c.clase('diasPrimer')}>
                Primer aviso
                <span className="con-sufijo">
                  <input id={id('primer')} type="number" min={1} max={90} value={v.diasPrimer ?? 45} onChange={(e) => setV({ ...v, diasPrimer: num(e.target.value) })} />
                  días antes
                </span>
                <Antes c={c} campo="diasPrimer" />
              </label>
              <label className={c.clase('diasSegundo')}>
                Segundo aviso
                <span className="con-sufijo">
                  <input id={id('segundo')} type="number" min={0} max={89} value={v.diasSegundo ?? 25} onChange={(e) => setV({ ...v, diasSegundo: num(e.target.value) })} />
                  días antes
                </span>
                <Antes c={c} campo="diasSegundo" />
              </label>
              <label className={c.clase('hora')}>
                Hora de envío
                <select id={id('hora')} value={v.hora ?? 7} onChange={(e) => setV({ ...v, hora: Number(e.target.value) })}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {h === 0 ? '12:00 a. m.' : h < 12 ? `${h}:00 a. m.` : h === 12 ? '12:00 p. m.' : `${h - 12}:00 p. m.`}
                    </option>
                  ))}
                </select>
                <Antes c={c} campo="hora" />
              </label>
              <label className={c.clase('copiasPrimer')}>
                Con copia a (primer aviso)
                <input id={id('copias1')} value={v.copiasPrimer} onChange={(e) => setV({ ...v, copiasPrimer: e.target.value })} placeholder="nadie" />
                <Antes c={c} campo="copiasPrimer" />
              </label>
              <label className={c.clase('copiasSegundo')}>
                Con copia a (segundo aviso)
                <input id={id('copias2')} value={v.copiasSegundo} onChange={(e) => setV({ ...v, copiasSegundo: e.target.value })} placeholder="kmora@xmartek.com" />
                <Antes c={c} campo="copiasSegundo" />
              </label>
              <p className="note" style={{ margin: 0, gridColumn: '1 / -1' }}>
                {!copiasOk ? 'Revisa las copias: correos completos separados por coma.' : tramosOk
                  ? `Cada día a esa hora (Colombia) sale un correo por Partner: el primer aviso cuando a una licencia le falten ${primer} días o menos, y el segundo cuando le falten ${segundo} o menos. Cada aviso sale una sola vez; si ya pasó el primer tramo, sale directo el segundo. Las copias valen para español e inglés; varios correos, separados por coma.`
                  : 'Revisa los días: el primer aviso va de 1 a 90 y el segundo debe ser menor que el primero.'}
              </p>
            </div>
          ) : null}
          {d.interno ? (
            <div className="programacion">
              <label className={c.clase('destinatarios')}>
                Enviar a
                <input id={id('para')} value={v.destinatarios} onChange={(e) => setV({ ...v, destinatarios: e.target.value })} placeholder="kmora@xmartek.com" />
                <Antes c={c} campo="destinatarios" />
              </label>
              <label className={c.clase('diasPrimer')}>
                Avisar
                <span className="con-sufijo">
                  <input id={id('dias')} type="number" min={1} max={90} value={v.diasPrimer ?? 15} onChange={(e) => setV({ ...v, diasPrimer: num(e.target.value) })} />
                  días antes
                </span>
                <Antes c={c} campo="diasPrimer" />
              </label>
              <label className={c.clase('hora')}>
                Hora de envío
                <select id={id('hora-interna')} value={v.hora ?? 8} onChange={(e) => setV({ ...v, hora: Number(e.target.value) })}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {h === 0 ? '12:00 a. m.' : h < 12 ? `${h}:00 a. m.` : h === 12 ? '12:00 p. m.' : `${h - 12}:00 p. m.`}
                    </option>
                  ))}
                </select>
                <Antes c={c} campo="hora" />
              </label>
              <p className="note" style={{ margin: 0, gridColumn: '1 / -1' }}>
                {tramosOk
                  ? `Un correo al día, a esa hora (Colombia), a ${v.destinatarios || '—'} con las licencias a las que ese día les faltan ${diasInterno} días para vencer (cada una se avisa una sola vez). Es independiente de los avisos a Partners. Para varias personas, sepáralas con coma.`
                  : 'Revisa los correos (separados por coma) y los días (de 1 a 90).'}
              </p>
            </div>
          ) : null}
          <label className={c.clase('asunto')}>
            Asunto
            <input id={id('asunto')} value={v.asunto} onChange={(e) => setV({ ...v, asunto: e.target.value })} />
            <Antes c={c} campo="asunto" />
          </label>
          <label className={c.clase('cuerpo')}>
            Texto
            <textarea id={id('cuerpo')} rows={8} value={v.cuerpo} onChange={(e) => setV({ ...v, cuerpo: e.target.value })} />
            <Antes c={c} campo="cuerpo" />
          </label>
          <p className="note" style={{ margin: 0 }}>
            Puedes usar: {d.variables.join(', ')}.{esLicencia ? ' Para negrita escribe **así**. {{licencias.tabla}} se convierte en una tabla; la firma del buzón va al final.' : ''}
          </p>
          <div className="vista-previa">
            <span className="lbl">{d.interno ? 'Así le llegará' : 'Así lo verá el cliente'}</span>
            <b>{rellenar(v.asunto, datosPrevia)}</b>
            {htmlPrevia ? <div className="correo-html" dangerouslySetInnerHTML={{ __html: htmlPrevia }} /> : <div style={{ whiteSpace: 'pre-wrap' }}>{rellenar(v.cuerpo, datosPrevia)}</div>}
          </div>
          <label className={c.clase('activa', 'opcion')}>
            <input id={id('activa')} type="checkbox" checked={v.activa} onChange={(e) => setV({ ...v, activa: e.target.checked })} /> Enviar automáticamente
            <Antes c={c} campo="activa" />
          </label>
          {activando ? (
            <label className="opcion aviso">
              <input id={id('entendido')} type="checkbox" checked={entendido} onChange={(e) => setEntendido(e.target.checked)} /> {d.interno ? 'Entiendo que desde ahora saldrá este correo interno cada día.' : 'Entiendo que desde ahora saldrán correos reales a los clientes.'}
            </label>
          ) : null}
          {d.conBuzon ? (
            <div className="prueba">
              <span>Enviar prueba a</span>
              <input id={id('prueba')} value={paraPrueba} onChange={(e) => setParaPrueba(e.target.value)} placeholder={yo.email ?? 'tu@correo.com'} />
              <button type="button" className="btn sm" onClick={() => void enviarPrueba()} disabled={probando || !buzonElegido?.cuentaId}>
                {probando ? 'Enviando…' : 'Enviar prueba'}
              </button>
              {!buzonElegido ? <span className="note">Elige primero el buzón que envía.</span> : null}
            </div>
          ) : null}
          <div className="actions">
            <button type="button" className={`btn ${confirmarBorrar ? 'danger' : 'ghost'}`} onClick={() => void borrar()} style={{ marginRight: 'auto' }}>
              {confirmarBorrar ? 'Sí, borrar (deja de enviar)' : 'Borrar automatización'}
            </button>
            <button className="btn primary" onClick={() => void guardar()} disabled={!puedeGuardar || guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      ) : null}
      {c.sucio && !abierta ? (
        <p className="note cp-nota" style={{ margin: 0 }}>
          {c.n === 1 ? 'Tiene 1 cambio sin guardar.' : `Tiene ${c.n} cambios sin guardar.`}{' '}
          <button type="button" className="btn sm ghost" onClick={() => setAbierta(true)}>
            Ver
          </button>
        </p>
      ) : null}
      <BarraCambios />
    </section>
  );
}
